import { useRef, useState } from 'react';

import { SignOutBlockedError } from './sign-out-blocked-error';

import type { SignOutOptions } from './auth-context';

/**
 * Keeps sign-out failures visible, prevents duplicate revocation requests and offers
 * "sign out anyway" once preparation has failed, so nobody is trapped in a session.
 */
export function useSignOutAction(signOut: (options?: SignOutOptions) => Promise<void>) {
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workshopActivityId, setWorkshopActivityId] = useState<string | null | undefined>();
  async function attempt(options?: SignOutOptions) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    setWorkshopActivityId(undefined);
    try {
      await signOut(options);
    } catch (error) {
      if (error instanceof SignOutBlockedError) setWorkshopActivityId(error.workshopActivityId);
      setError(
        error instanceof SignOutBlockedError
          ? error.message
          : 'Could not finish signing out. Check your connection and try again.',
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  return {
    run: () => attempt(),
    force: () => attempt({ force: true }),
    busy,
    error,
    /** Shown after a failed attempt: the learner may proceed without workspace preparation. */
    canForce: error !== null,
    workshopActivityId,
  };
}
